import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, BookmarkCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, asList, errorText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MARK = { blue: "bg-[#D6E0FA]", yellow: "bg-[#FBEFB8]", grey: "bg-[#E2E5EA]" };

export function useArticleTools(slug) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [saved, setSaved] = useState(false);
  const [highlights, setHighlights] = useState([]);

  useEffect(() => {
    if (!user || !slug) { setSaved(false); setHighlights([]); return undefined; }
    let alive = true;
    api.get(`/me/saved/${slug}`).then((r) => { if (alive) setSaved(Boolean(r.data?.saved)); }).catch(() => {});
    api.get(`/me/highlights/${slug}`).then((r) => { if (alive) setHighlights(asList(r.data)); }).catch(() => {});
    return () => { alive = false; };
  }, [user, slug]);

  const needAccount = useCallback(() => {
    toast("Sign in to save studies and keep highlights.");
    nav(`/account?next=/article/${slug}`);
  }, [nav, slug]);

  const toggleSave = async () => {
    if (!user) return needAccount();
    try {
      const r = saved ? await api.delete(`/me/saved/${slug}`) : await api.post(`/me/saved/${slug}`);
      setSaved(Boolean(r.data?.saved));
      toast.success(r.data?.saved ? "Saved to your library" : "Removed from your library");
    } catch (e) { toast.error(errorText(e)); }
    return null;
  };

  const addHighlight = async ({ text, para }) => {
    if (!user) return needAccount();
    try {
      const { data } = await api.post("/me/highlights", { slug, para, text, color: "blue" });
      setHighlights((h) => [...h, data]);
      toast.success("Highlighted");
    } catch (e) { toast.error(errorText(e)); }
    return null;
  };

  const removeHighlight = async (id) => {
    try { await api.delete(`/me/highlights/${id}`); setHighlights((h) => h.filter((x) => x.id !== id)); }
    catch (e) { toast.error(errorText(e)); }
  };

  return { saved, toggleSave, highlights, addHighlight, removeHighlight };
}

export function SaveButton({ saved, onClick }) {
  const Icon = saved ? BookmarkCheck : Bookmark;
  return (
    <button onClick={onClick} data-testid="save-article-btn"
      className={`h-10 px-4 inline-flex items-center gap-2 text-sm font-semibold border transition-colors ${saved ? "bg-[var(--red)] text-white border-[var(--red)]" : "border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"}`}>
      <Icon size={16} /> {saved ? "Saved" : "Save study"}
    </button>
  );
}

export function MarkedText({ text, marks }) {
  const ranges = [];
  marks.forEach((m) => {
    const i = text.indexOf(m.text);
    if (i >= 0 && !ranges.some((r) => i < r.end && i + m.text.length > r.start)) ranges.push({ start: i, end: i + m.text.length, m });
  });
  ranges.sort((a, b) => a.start - b.start);
  const out = [];
  let pos = 0;
  ranges.forEach((r, k) => {
    if (r.start > pos) out.push(text.slice(pos, r.start));
    out.push(<mark key={k} className={`${MARK[r.m.color] || MARK.blue} text-inherit px-0.5`} data-testid="article-highlight">{text.slice(r.start, r.end)}</mark>);
    pos = r.end;
  });
  out.push(text.slice(pos));
  return out;
}

export function HighlightList({ highlights, onRemove }) {
  if (!highlights.length) return null;
  return (
    <div data-testid="highlight-list">
      <p className="eyebrow mb-3">Your highlights</p>
      <ul className="space-y-3">
        {highlights.map((h) => (
          <li key={h.id} className="text-xs leading-relaxed border-l-2 border-[var(--red)] pl-3 flex gap-2 justify-between">
            <span className="text-[var(--ink-2)] line-clamp-3">{h.text}</span>
            <button onClick={() => onRemove(h.id)} aria-label="Remove highlight" className="text-[var(--muted)] hover:text-[var(--red)] shrink-0" data-testid={`remove-highlight-${h.id}`}><X size={13} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
